import { jwtDecode } from 'jwt-decode'
import { defineStore, getActivePinia } from 'pinia'

/**
 * Exchanges an authorization code for OAuth tokens and stores them in session storage.
 * @param code - The authorization code received from the OAuth callback
 * @returns The access token, or null if unavailable
 * @throws {Error} If the token request fails
 */
export const getOAuthToken = async (code: string): Promise<string | null> => {
	const params = new URLSearchParams({
		client_id: import.meta.env.VITE_CLIENT_KEY,
		code,
		code_verifier: sessionStorage.getItem('code_verifier') ?? '',
		grant_type: 'authorization_code',
		redirect_uri: `${window.location.origin}/auth-success`
	})

	const response = await fetch(import.meta.env.VITE_OIDC_TOKEN, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/x-www-form-urlencoded'
		},
		body: params.toString()
	})

	if (!response.ok) {
		throw new Error(`Token request failed: ${response.status} ${response.statusText}`)
	}

	const data = await response.json()

	sessionStorage.setItem('access_token', data.access_token)
	sessionStorage.setItem('refresh_token', data.refresh_token)

	const decoded = jwtDecode(data.access_token)
	sessionStorage.setItem('logged_in_user', decoded.sub ?? '')

	return sessionStorage.getItem('access_token')
}

/**
 * Refreshes the OAuth access token using the stored refresh token.
 * @throws {Error} If the token refresh request fails
 */
export const refreshAuthToken = async (): Promise<void> => {
	const params = new URLSearchParams({
		client_id: import.meta.env.VITE_CLIENT_KEY,
		grant_type: 'refresh_token',
		refresh_token: sessionStorage.getItem('refresh_token') ?? ''
	})

	const response = await fetch(import.meta.env.VITE_OIDC_TOKEN, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/x-www-form-urlencoded'
		},
		body: params.toString()
	})

	if (!response.ok) {
		throw new Error(`Token refresh failed: ${response.status} ${response.statusText}`)
	}

	const data = await response.json()
	sessionStorage.setItem('access_token', data.access_token)
}

/**
 * Checks whether the stored access token exists and has not expired.
 * @returns `true` if the token is valid, `false` otherwise
 */
export const isTokenValid = (): boolean => {
	const token = sessionStorage.getItem('access_token')
	if (!token) return false

	const { exp } = jwtDecode(token)
	return !!exp && exp >= Date.now() / 1000
}

/** Keys to persist in session storage across logout */
const SESSION_KEYS_TO_PRESERVE = ['deep_link', 'deepLinkMessage', 'userNotice', 'uod'] as const

/**
 * Logs out the user by clearing session storage and resetting all Pinia stores,
 * while preserving select session keys.
 */
export const logout = (): void => {
	const preserved = Object.fromEntries(
		SESSION_KEYS_TO_PRESERVE.map((key) => [key, sessionStorage.getItem(key)])
	)

	sessionStorage.clear()
	resetAllStores()

	Object.entries(preserved).forEach(([key, value]) => {
		if (value) sessionStorage.setItem(key, value)
	})
}

/**
 * Resets all active Pinia stores to their initial state.
 */
const resetAllStores = (): void => {
	const activePinia = getActivePinia()
	if (!activePinia) return

	Object.entries(activePinia.state.value).forEach(([storeName, state]) => {
		const store = defineStore(storeName, state)(activePinia)
		store.$reset()
	})
}
